import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useFormDetailsQuery, invalidatePublicQueries } from '../lib/PublicQueries.js';
import Loading from '../../components/Loading';
import Question from '../../components/forms/Question';
import usePublicPostApi from '../lib/usePublicPostApi.js';
import { ErrorContext } from '../../lib/ErrorContext';
import FormSection from '../components/FormSection.jsx';
import useFormSections from '../lib/useFormSections.js';
import FormThankYou from '../components/FormThankYou.jsx';
import FormHeaderProgress from '../components/FormHeaderProgress.jsx';
import { ArrowRightIcon } from '@heroicons/react/24/outline/index.js';
import ThemedForm from '../../components/forms/ThemedForm.jsx';
import FormPaymentForm from '../components/PaymentForm.jsx';
import ErrorMessage from '../../components/ErrorMessage';
import ErrorMessageBlock from '../../components/ErrorMessageBlock.jsx';
import PoweredBy from '../components/PoweredBy.jsx';
import FormContextProvider, {
    calculateEventQuantity,
} from '../../components/forms/FormContext.jsx';
import FormHeaderBlock from '../../components/forms/FormHeaderBlock.jsx';
import PageContainer from '../components/PageContainer.jsx';
import FormBackgroundContainer from '../../components/forms/FormBackgroundContainer.jsx';
import { useSetCurrency } from '../../lib/CurrencyHelpers.js';
import EventTrackingIntegration from '../components/EventTrackingIntegration.jsx';
import FormViewSections from '../components/FormViewSections.jsx';
import * as Toast from '~/lib/Toast';
import { getBrowserFingerprint } from '~/lib/BrowserFingerprint';
import { useSetLocale } from '~/lib/useSetLocale.js';
import { sendScrollTop } from '~/public/lib/EmbedMessaging.js';
import * as Sentry from '@sentry/react';
import { isEmbedded } from '../lib/PageHelpers.js';
import PreviewBookingBanner from '~/public/components/PreviewBookingBanner.jsx';


const FormViewPage = ({}) => {
    const { businessId, formId } = useParams();
    const url = new URL(document.location.href);
    const { data, isLoading } = useFormDetailsQuery(businessId, formId);

    const [formData, setFormData] = useState({});
    const [formSubmissionResults, setFormSubmissionResults] = useState(null);

    const { save, isSaving: isSubmitting } = usePublicPostApi(
        `/forms/${formId}`,
    );
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    const [paymentSettings, setPaymentSettings] = useState(false);

    useSetLocale(data?.context);

    useEffect(() => {
        if (data?.initialData && typeof data?.initialData === 'object') {
            setFormData(old => {
                console.log('setting initial data to', {
                    ...old,
                    ...data?.initialData,
                });
                return {
                    ...old,
                    ...data?.initialData,
                };
            });
        }
    }, [JSON.stringify(data?.initialData)]);

    const form = data?.form || {};
    const sections = (data?.formSections || []).filter(
        d => d.questions.length > 0,
    );
    const canPay = !!data?.canPay;
    const hasEvents = calculateEventQuantity(formData) > 0;

    useEffect(() => {
        if (form.name) {
            document.title = `${form.business?.name} - ${form.name}`;
        }
    }, [form.name]);

    // Check if form is embed-only and redirect if not embedded
    useEffect(() => {
        const styles = form?.settings?.styles;
        if (styles?.embedOnly && !isEmbedded()) {
            let redirectUrl = styles?.embedOnlyRedirectUrl;
            if (redirectUrl) {
                // Prepend https:// if the URL doesn't start with http:// or https://
                if (!redirectUrl.toLowerCase().startsWith('http://') && !redirectUrl.toLowerCase().startsWith('https://')) {
                    redirectUrl = 'https://' + redirectUrl;
                }
                window.location.href = redirectUrl;
            }
        }
    }, [form?.settings?.styles]);

    // Listen for postMessage to invalidate queries
    useEffect(() => {
        const handleMessage = (event) => {
            // Security: Verify the message is from our own origin
            const allowedOrigins = [window.location.origin, 'http://localhost', 'https://app.breely.com'];
            if (!allowedOrigins.includes(event.origin)) {
                console.log('Ignoring postMessage from different origin:', event.origin);
                return;
            }

            if (event.data === 'invalidate-queries') {
                console.log('Received invalidate-queries message, invalidating public queries');
                invalidatePublicQueries();
            }
        };

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, []);

    const {
        visibleSections,
        setVisibleSections,
        showNextSection,
        submitIsNext,
        validate,
        hasPaymentStep,
    } = useFormSections(sections, formData, canPay);

    function submit(ev) {
        ev.preventDefault();
        setSubmitError(null);

        if (validate()) {
            save(
                {
                    answers: formData,
                    visibleSections: visibleSections,
                    previousSubmissionToken:
                        formSubmissionResults?.submission?.token,
                    fp: getBrowserFingerprint(),
                    params: url.searchParams.toString(),
                },
                {
                    onErrorMessage: error => {
                        // If error contains 'Server Error' then send to Sentry
                        if (error?.includes('Server Error') || error?.includes('Unavailable')) {
                            Sentry.captureMessage(`Form submission failed: ${error} (${formId}) - ${document.location.href}`);
                            setSubmitError('Failed to submit form, please try again');
                        } else {
                            setSubmitError(error);
                        }

                        setTimeout(() => {
                            // If it applies to a question jump back to that section
                            const errorEls = document.querySelectorAll(
                                '.form-section-visible .is-error',
                            );
                            if (errorEls.length > 0) {
                                const lastError = errorEls[errorEls.length - 1];
                                if (typeof lastError.closest === 'function') {
                                    const erroredSection = lastError.closest(
                                        '.form-section-visible',
                                    ).dataset.sectionid;
                                    const visibleSectionIdx =
                                        visibleSections.indexOf(
                                            parseInt(erroredSection),
                                        );

                                    if (
                                        visibleSectionIdx > -1 &&
                                        visibleSectionIdx <
                                            visibleSections.length - 1
                                    ) {
                                        // Jump back to the errored section
                                        // Because we're jumping and it's disorienting if the section is big
                                        //  also show a toast saying why
                                        Toast.error(error);
                                        setVisibleSections(
                                            visibleSections.slice(
                                                0,
                                                visibleSectionIdx,
                                            ),
                                        );
                                    }
                                }
                            }
                        }, 0);
                    },
                },
            ).then(result => {
                if (result?.submission || result?.payment) {
                    // It's valid!
                    sendScrollTop();
                    setHasSubmitted(true);
                    setPaymentSettings(result.payment);
                    setFormSubmissionResults(result);
                } else {
                    setSubmitError('Something went wrong, please try again.');
                }
            });
        } else {
            // Focus on first error so user scrolls to it
            setTimeout(() => {
                const firstErrorContainer =
                    document.getElementsByClassName('is-error')[0];
                firstErrorContainer?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                    inline: 'nearest',
                });
            }, 100);
        }
    }

    if (
        !!data &&
        !data?.hasClientIdentifier &&
        !formData?.client &&
        !isLoading
    ) {
        return (
            <ThemedForm styles={form?.settings?.styles}>
                <ErrorMessageBlock>
                    Sorry, this form is unavailable because there is no client
                    identifier. Please contact the business.
                </ErrorMessageBlock>
            </ThemedForm>
        );
    }

    if (!!data && form?.disabled && !isLoading) {
        return (
            <ThemedForm styles={form?.settings?.styles}>
                <ErrorMessageBlock>
                    This form is currently disabled and not accepting submissions.
                    Please contact the business for assistance.
                </ErrorMessageBlock>
            </ThemedForm>
        );
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center">
                <Loading className="text-lg mt-8" />
            </div>
        );
    }

    return (
        <ThemedForm styles={form?.settings?.styles}>
            <PreviewBookingBanner form={form} />
            <FormBackgroundContainer styles={form?.settings?.styles}>
                <FormContextProvider
                    businessId={businessId}
                    formId={formId}
                    formData={formData}
                    setFormData={setFormData}
                    sections={sections}
                >
                    <FormHeaderBlock
                        styles={form?.settings?.styles}
                        formId={form?.id}
                        className="mt-0"
                    />

                    <PageContainer>
                        <>
                            <FormHeaderProgress
                                form={form}
                                visibleSections={visibleSections}
                                setVisibleSections={setVisibleSections}
                                sections={sections}
                                answers={formData}
                                hasSubmitted={hasSubmitted}
                                hasPaymentStep={hasPaymentStep}
                                isOnPaymentStep={
                                    !!paymentSettings && hasSubmitted
                                }
                                onPaymentStepBack={() => setHasSubmitted(false)}
                            />
                            {!hasSubmitted && (
                                <FormViewSections
                                    sections={sections}
                                    submit={submit}
                                    formData={formData}
                                    setFormData={setFormData}
                                    showNextSection={showNextSection}
                                    submitError={submitError}
                                    setSubmitError={setSubmitError}
                                    isSubmitting={isSubmitting}
                                    hasPaymentStep={hasPaymentStep}
                                    submitIsNext={submitIsNext}
                                    hasEvents={hasEvents}
                                    visibleSections={visibleSections}
                                    styles={form?.settings?.styles}
                                />
                            )}

                            {hasSubmitted && !!paymentSettings && (
                                <FormPaymentForm
                                    paymentSettings={paymentSettings}
                                    submission={
                                        formSubmissionResults.submission
                                    }
                                    lineItems={formSubmissionResults.lineItems}
                                />
                            )}

                            {hasSubmitted && !paymentSettings && (
                                <FormThankYou
                                    form={form}
                                    answers={formData}
                                    sections={data?.sections || []}
                                    formSubmissionResults={
                                        formSubmissionResults
                                    }
                                />
                            )}
                        </>
                    </PageContainer>
                    <EventTrackingIntegration
                        integrationSettings={data?.integrations}
                    />
                </FormContextProvider>
            </FormBackgroundContainer>
        </ThemedForm>
    );
};

export default FormViewPage;
